import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { startGoogleSignIn } from "@/lib/googleAuth";
import { AdminShell } from "@/pages/AdminHome";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field = { backgroundColor: "#241246", border: "1px solid #1A0F3C", color: "#F5EFE3" };
const inputCls = "h-12 w-full rounded-md px-4 text-sm outline-none";
const goldBtn =
  "inline-flex items-center justify-center whitespace-nowrap rounded-full px-6 h-11 text-xs uppercase transition-colors hover:bg-[#F2D589]/[0.12] disabled:opacity-50";
const goldStyle = {
  backgroundColor: "transparent",
  color: "#F2D589",
  border: "1px solid #F2D589",
  letterSpacing: "0.18em",
  fontWeight: 500,
};

function Label({ children }) {
  return (
    <span className="block text-[11px] uppercase mb-2" style={{ color: "#C6BBA9", letterSpacing: "0.2em" }}>
      {children}
    </span>
  );
}

export default function AnnouncementAdmin() {
  const { user, loading, loginEmail } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({ enabled: true, text: "", url: "/community-events", starts_at: "", ends_at: "" });
  const [version, setVersion] = useState(1);
  const [saving, setSaving] = useState(false);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/announcement`);
      setForm({
        enabled: data.enabled_raw ?? data.enabled,
        text: data.text || "",
        url: data.url || "/community-events",
        starts_at: data.starts_at || "",
        ends_at: data.ends_at || "",
      });
      setVersion(data.version || 1);
      setDenied(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/announcement`, form, { withCredentials: true });
      setVersion(data.version);
      if (!form.enabled || !data.text) toast.success("Bar hidden.");
      else if (!data.enabled) toast.success("Saved — scheduled, not showing yet.");
      else toast.success("Bar updated.");
    } catch (err) {
      if (err?.response?.status === 403) setDenied(true);
      else toast.error(formatApiErrorDetail(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminShell active="announcement">
        <p className="mt-20 text-sm" style={{ color: "#C6BBA9" }}>
          One moment…
        </p>
      </AdminShell>
    );
  }

  if (!user) {
    return (
      <AdminShell active="announcement">
        <div className="mt-16 max-w-[24rem]" data-testid="announcement-admin-signin">
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}>
            Announcement
          </h1>
          <button
            type="button"
            onClick={() => startGoogleSignIn("/admin/announcement")}
            className={`${goldBtn} mt-8 w-full`}
            style={goldStyle}
            data-testid="announcement-admin-google"
          >
            Continue with Google
          </button>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                await loginEmail(email.trim(), password);
              } catch (err) {
                toast.error(formatApiErrorDetail(err));
              } finally {
                setBusy(false);
              }
            }}
            className="mt-8 space-y-3"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your address"
              className={inputCls}
              style={field}
              data-testid="announcement-admin-email"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="password"
              className={inputCls}
              style={field}
              data-testid="announcement-admin-password"
            />
            <button type="submit" disabled={busy} className={`${goldBtn} w-full`} style={goldStyle} data-testid="announcement-admin-signin-submit">
              {busy ? "…" : "Sign in"}
            </button>
          </form>
        </div>
      </AdminShell>
    );
  }

  if (denied || !user.is_admin) {
    return (
      <AdminShell active="announcement">
        <p className="mt-20 text-sm" style={{ color: "#C6BBA9" }} data-testid="announcement-admin-denied">
          That account isn&apos;t on the admin list.
        </p>
      </AdminShell>
    );
  }

  return (
    <AdminShell active="announcement">
      <div className="mt-14">
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}>
          Announcement
        </h1>
        <p className="mt-3 text-sm" style={{ color: "#C6BBA9" }}>
          The gold bar across the top of every page. Change the words and anyone
          who dismissed the old one will see the new one.
        </p>
      </div>

      {form.text ? (
        <div className="mt-8" data-testid="announcement-admin-preview">
          <Label>Preview</Label>
          <div
            className="rounded-md px-6 py-2.5 text-center text-xs uppercase"
            style={{ backgroundColor: "#F2D589", color: "#080419", letterSpacing: "0.14em", fontWeight: 700 }}
          >
            {form.text} &rarr;
          </div>
        </div>
      ) : null}

      <form onSubmit={save} className="mt-10 grid gap-5 max-w-[46rem]" data-testid="announcement-admin-form">
        <div>
          <Label>Announcement text</Label>
          <input
            value={form.text}
            onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
            placeholder="Now Booking 2026/5787 Texas Tour — …"
            className={inputCls}
            style={field}
            data-testid="announcement-form-text"
          />
        </div>
        <div>
          <Label>Where it links (a site path like /community-events, or a full https:// link)</Label>
          <input
            value={form.url}
            onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
            placeholder="/community-events"
            className={inputCls}
            style={field}
            data-testid="announcement-form-url"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label>Starts (optional — blank means right now)</Label>
            <input
              type="date"
              value={form.starts_at}
              onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
              className={inputCls}
              style={field}
              data-testid="announcement-form-starts"
            />
          </div>
          <div>
            <Label>Retires after (optional — blank means it stays up)</Label>
            <input
              type="date"
              value={form.ends_at}
              onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
              className={inputCls}
              style={field}
              data-testid="announcement-form-ends"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-5">
          <label className="flex items-center gap-3 text-xs uppercase" style={{ color: "#C6BBA9", letterSpacing: "0.18em" }}>
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              data-testid="announcement-form-enabled"
            />
            Show the bar
          </label>
          <button type="submit" disabled={saving} className={goldBtn} style={goldStyle} data-testid="announcement-form-save">
            {saving ? "Saving…" : "Save"}
          </button>
          <span className="text-[11px] uppercase" style={{ color: "#C6BBA9", letterSpacing: "0.16em" }}>
            v{version}
          </span>
        </div>
      </form>
    </AdminShell>
  );
}
